import "./dashboard.css";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Calendar } from "primereact/calendar";
import { DASHBOARD_URL } from "../../global/API";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import CustomChart from "../../components/customChart/customChart";

const Dashboard = () => {
  const [dashboardCount, setDashboardCount] = useState(null);
  const [dates, setDates] = useState({ from: null, to: null });
  const [selectedLabel, setSelectedLabel] = useState("Today");

  const intakeLabels = ["Pending Contact", "Consult Scheduled", "Pending Retained", "Pending Engagement", "Unqualified"];
  const intakeValues = [
    dashboardCount?.intakeBreakdown?.pendingContact || 0,
    dashboardCount?.intakeBreakdown?.consultScheduled || 0,
    dashboardCount?.intakeBreakdown?.pendingRetained || 0,
    dashboardCount?.intakeBreakdown?.pendingEngagement || 0,
    dashboardCount?.intakeBreakdown?.unqualified || 0,
  ];
  const intakeTooltipLabels = intakeLabels.map((label, idx) => {
    return `${label} (${intakeValues[idx]})`;
  });

  const fetchData = async () => {
    const jwtToken = Cookies.get("jwtToken");

    const options = {
      method: "GET",
      headers: { Authorization: `Bearer ${jwtToken}`, Accept: "application/json" },
    };

    let query = ``;

    if (dates?.[0]) {
      query += `?fdate=${format(new Date(dates[0]), "yyyy-MM-dd")}`;
    }
    if (dates?.[1]) {
      query += `&tdate=${format(new Date(dates[1]), "yyyy-MM-dd")}`;
    }

    const URL = DASHBOARD_URL + query;

    try {
      const response = await fetch(URL, options);
      const data = await response.json();
      if (data.status === "Success") {
        setDashboardCount(data);
        const intake = data.matter_status_category?.find((s) => s.status_category === "intake")?.count || 0;
        const hired = data.matter_status_category?.find((s) => s.status_category === "hired")?.count || 0;
        const declined = data.matter_status_category?.find((s) => s.status_category === "declined")?.count || 0;

        const intakeBreakdown = {
          pendingContact: data.matter_status?.find((s) => s.status === "Pending Contact")?.count || 0,
          consultScheduled: data.matter_status?.find((s) => s.status === "Consult Scheduled")?.count || 0,
          pendingRetained: data.matter_status?.find((s) => s.status === "Pending Retained")?.count || 0,
          pendingEngagement: data.matter_status?.find((s) => s.status === "Pending Engagement")?.count || 0,
          unqualified: data.matter_status?.find((s) => s.status === "Unqualified")?.count || 0,
          qualified: data.matter_status?.find((s) => s.status === "qualified")?.count || 0,
        };

        setDashboardCount({
          ...data,
          intake,
          hired,
          declined,
          intakeBreakdown,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const setQuickRange = (type) => {
    const today = new Date();

    switch (type) {
      case "today":
        setDates([startOfDay(today), endOfDay(today)]);
        setSelectedLabel("Today");
        break;
      case "yesterday":
        const yesterday = subDays(today, 1);
        setDates([startOfDay(yesterday), endOfDay(yesterday)]);
        setSelectedLabel("Yesterday");
        break;
      case "7days":
        setDates([subDays(today, 6), endOfDay(today)]);
        setSelectedLabel("Last 7 Days");
        break;
      case "30days":
        setDates([subDays(today, 29), endOfDay(today)]);
        setSelectedLabel("Last 30 Days");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    fetchData();
    setQuickRange();
  }, [dates]);

  const handleReset = () => {
    setDates("");
    setSelectedLabel("Today");
  };

  return (
    <div className="page-content dashboard-page">
      <div className="container-fluid">
        <div className="row section">
          <div className="col-12">
            <div className="page-title-box">
              <h3 className="mb-0 ">Dashboard</h3>
              <div className="page-title-box-content">
                <button type="button" className="page-title-btn refresh-button" title="Reset" onClick={handleReset}>
                  <i className="ri-refresh-line"></i>
                </button>
                <div className="dates-group">
                  <Calendar
                    style={{ width: "230px", marginRight: "0" }}
                    value={dates}
                    onChange={(e) => setDates(e.value)}
                    selectionMode="range"
                    readOnlyInput
                    hideOnRangeSelection
                    placeholder="Date range filter"
                    dateFormat="M dd yy"
                    // showButtonBar
                    showIcon
                  />
                </div>
                <div className="dropdown">
                  <button type="button" className="topbar-button" id="page-header-user-dropdown" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                    <span className="d-flex align-items-center">
                      <i className="ri-calendar-event-line"></i>
                      {selectedLabel}
                    </span>
                  </button>
                  <div className="dropdown-menu dropdown-menu-end">
                    <button type="button" className="dropdown-item" onClick={() => setQuickRange("today")}>
                      <i className="ri-calendar-event-line"></i>
                      <span>Today</span>
                    </button>
                    <button type="button" className="dropdown-item" onClick={() => setQuickRange("yesterday")}>
                      <i className="ri-calendar-event-line"></i>
                      <span>Yesterday</span>
                    </button>
                    <button type="button" className="dropdown-item" onClick={() => setQuickRange("7days")}>
                      <i className="ri-calendar-event-line"></i>
                      <span>Last 7 days</span>
                    </button>
                    <button type="button" className="dropdown-item" onClick={() => setQuickRange("30days")}>
                      <i className="ri-calendar-event-line"></i>
                      <span>Last 30 days</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="zoom-history section">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box page-title-box-dashboard">
                <h4 className="mb-0 fw-semibold">Zoom overview</h4>
              </div>
            </div>
          </div>
          <div id="listBoxes" className="listBoxes">
            <div className="row">
              <div className="col-md-6 col-xl-3 ">
                <a href="#!" className="card">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h4 className="card-title mb-2 ">Inbound Calls</h4>
                        <p className="text-muted fw-medium fs-16 mb-0">
                          <i className="ri-arrow-down-line"></i> {dashboardCount?.zoom_inbound || 0}
                        </p>
                      </div>
                      <div>
                        <h4 className="card-title mb-2 ">Hours</h4>
                        <p className="text-muted fw-medium fs-16 mb-0">{dashboardCount?.zoom_inbound_duration || "00:00:00"}</p>
                      </div>
                      <div>
                        <div className="avatar-sm bg-primary bg-opacity-10 rounded">
                          <Icon icon="solar:incoming-call-linear" className="fs-32 text-primary avatar-title"></Icon>
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
              <div className="col-md-6 col-xl-3 ">
                <a href="#!" className="card">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h4 className="card-title mb-2 ">Outbound Calls</h4>
                        <p className="text-muted fw-medium fs-16 mb-0">
                          <i className="ri-arrow-up-line"></i> {dashboardCount?.zoom_outbound || 0}
                        </p>
                      </div>
                      <div>
                        <h4 className="card-title mb-2 ">Hours</h4>
                        <p className="text-muted fw-medium fs-16 mb-0">{dashboardCount?.zoom_outbound_duration || "00:00:00"}</p>
                      </div>
                      <div>
                        <div className="avatar-sm bg-primary bg-opacity-10 rounded">
                          <Icon icon="solar:outgoing-call-linear" className="fs-32 text-primary avatar-title"></Icon>
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
              <div className="col-md-6 col-xl-3 ">
                <a href="#!" className="card">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h4 className="card-title mb-2 ">No Answer / Dropped</h4>
                        <p className="text-muted fw-medium fs-16 mb-0">
                          <i className="ri-close-line"></i> {dashboardCount?.zoom_noanswer || 0}
                        </p>
                      </div>
                      <div>
                        <div className="avatar-sm bg-primary bg-opacity-10 rounded">
                          <Icon icon="solar:call-cancel-linear" className="fs-32 text-primary avatar-title"></Icon>
                        </div>
                      </div>
                    </div>
                    {/* <div className="d-flex align-items-center justify-content-between mt-3">
                      <p className="mb-0">
                        <span className="text-success fw-medium mb-0">
                          <i className="ri-arrow-up-line"></i>34.4%
                        </span>{" "}
                        vs last month
                      </p>
                      <div>
                        <a href="#!" className="link-primary fw-medium">
                          See Details <i className="ri-arrow-right-line align-middle"></i>
                        </a>
                      </div>
                    </div> */}
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="matter-history section">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box page-title-box-dashboard">
                <h4 className="mb-0 fw-semibold">Clio overview</h4>
              </div>
              <div className="page-title-box-content"></div>
            </div>
          </div>
          <div id="listBoxes" className="listBoxes">
            <div className="row">
              <div className="col-md-6 col-xl-3">
                <a href="#!" className="card">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h4 className="card-title mb-2 ">Leads</h4>
                        <p className="text-muted fw-medium fs-16 mb-0">
                          <i className="ri-add-line"></i> {dashboardCount?.leads || 0}
                        </p>
                      </div>
                      <div>
                        <div className="avatar-sm bg-primary bg-opacity-10 rounded">
                          <i className="ri-folder-received-line fs-24 text-primary avatar-title"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
              <div className="col-md-6 col-xl-3">
                <a href="#!" className="card">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h4 className="card-title mb-2 ">Matters</h4>
                        <p className="text-muted fw-medium fs-16 mb-0">
                          <i className="ri-add-line"></i> {dashboardCount?.matter_total || 0}
                        </p>
                      </div>
                      <div>
                        <div className="avatar-sm bg-primary bg-opacity-10 rounded">
                          <i className="ri-folder-received-line fs-24 text-primary avatar-title"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
              <div className="col-md-6 col-xl-3">
                <a href="#!" className="card">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h4 className="card-title mb-2 ">Hired</h4>
                        <p className="text-muted fw-medium fs-16 mb-0">
                          <i className="ri-check-line"></i> {dashboardCount?.hired || 0}
                        </p>
                      </div>
                      <div>
                        <div className="avatar-sm bg-primary bg-opacity-10 rounded">
                          <i className="ri-folder-check-line fs-24 text-primary avatar-title"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
              <div className="col-md-6 col-xl-3">
                <a href="#!" className="card">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h4 className="card-title mb-2 ">Not hired</h4>
                        <p className="text-muted fw-medium fs-16 mb-0">
                          <i className="ri-close-line"></i> {dashboardCount?.declined || 0}
                        </p>
                      </div>
                      <div>
                        <div className="avatar-sm bg-primary bg-opacity-10 rounded">
                          <i className="ri-folder-close-line fs-24 text-primary avatar-title"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="intake-table section">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box page-title-box-dashboard">
                <h4 className="mb-0 fw-semibold">Pipeline Status</h4>
              </div>
              <div className="page-title-box-content"></div>
            </div>
          </div>
          <div className="row">
            <div className="col-xl-4">
              <div className="card">
                <div className="table-responsive">
                  <table className="table align-middle text-nowrap table-hover table-centered property-table">
                    <thead className="bg-light-subtle">
                      <tr style={{ background: "#f2f2f2" }}>
                        <th>
                          <strong> Pipeline Status table </strong>
                        </th>
                        <th></th>
                        {/* {dashboardCount?.intake || 0} */}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Pending Contact</td>
                        <td>{dashboardCount?.intakeBreakdown?.pendingContact || 0}</td>
                      </tr>
                      <tr>
                        <td>Consult Scheduled</td>
                        <td>{dashboardCount?.intakeBreakdown?.consultScheduled || 0}</td>
                      </tr>
                      <tr>
                        <td>Pending Retained</td>
                        <td>{dashboardCount?.intakeBreakdown?.pendingRetained || 0}</td>
                      </tr>
                      <tr>
                        <td>Pending Engagement</td>
                        <td>{dashboardCount?.intakeBreakdown?.pendingEngagement || 0}</td>
                      </tr>
                      <tr>
                        <td>Unqualified</td>
                        <td>{dashboardCount?.intakeBreakdown?.unqualified || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {dashboardCount?.intake === 0 ? (
              ""
            ) : (
              <div className="col-xl-5">
                <div className="chart-info">
                  <CustomChart chartLabels={intakeTooltipLabels} chartDetails={intakeValues} />
                  <ul>
                    <li>
                      <strong className="fs-18">Pipline Status Chart</strong>
                    </li>
                    <li>
                      <span className="blue-500"></span>Pending Contact
                    </li>
                    <li>
                      <span className="green-500"></span>Consult Scheduled
                    </li>
                    <li>
                      <span className="pink-500"></span>Pending Retained
                    </li>
                    <li>
                      <span className="yellow-500"></span>Pending Engagement
                    </li>
                    <li>
                      <span className="red-500"></span>Unqualified
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="assignee-history">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box page-title-box-dashboard">
                <h4 className="mb-0 fw-semibold">Intake specialist update</h4>
              </div>
              <div className="page-title-box-content"></div>
            </div>
          </div>
          <div className="row">
            <div className="col-xl-12">
              <div className="card">
                <div className="table-responsive">
                  <table className="table align-middle text-nowrap table-hover table-centered property-table">
                    <thead className="bg-light-subtle">
                      <tr>
                        <th>Name</th>
                        <th>Inbound</th>
                        <th>Total hours</th>
                        <th>Outbound</th>
                        <th>Total hours</th>
                        <th>Assigned matters</th>
                        <th>Pending contact</th>
                        <th>Consult Scheduled</th>
                        <th>Hired</th>
                        <th>Not hired</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardCount?.teams?.length > 0 ? (
                        dashboardCount.teams.map((team) => {
                          const { id, name, Inbound, Inbound_duration, Outbound, Outbound_duration, assigned_matters, pending_contact, consult_scheduled, hired, not_hired } = team;

                          return (
                            <tr key={id}>
                              <td>{name}</td>
                              <td>{Inbound}</td>
                              <td>{Inbound_duration}</td>
                              <td>{Outbound}</td>
                              <td>{Outbound_duration}</td>
                              <td>{assigned_matters}</td>
                              <td>{pending_contact}</td>
                              <td>{consult_scheduled}</td>
                              <td>{hired}</td>
                              <td>{not_hired}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="10" className="text-center py-4">
                            No records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
